import dynamic from 'next/dynamic';

import { ImageWithPlaceholder } from '@/components/ImageWithPlaceholder';
import { ImagePosition } from '@/lib/marketing-page/dto/modules';
import { replaceKeysFromString } from '@/lib/marketing-page/utils';
import { getBackgroundSectionClassName } from '@/utils/marketing-landing-page';

import { BackgroundColors, EyebrowType, ImageProps, LinkProps } from '../types';

import clsx from 'clsx';

const Eyebrow = dynamic(
  () => import('../shared/Eyebrow').then((mod) => mod.Eyebrow),
  { ssr: false }
);
const SecondaryHeading = dynamic(
  () => import('@/components/typography').then((mod) => mod.SecondaryHeading),
  { ssr: false }
);
const SecondaryLinkButton = dynamic(
  () => import('@/components/buttons').then((mod) => mod.SecondaryLinkButton),
  { ssr: false }
);

export type ImageLeftTextRightProps = {
  eyebrow?: string;
  title?: string;
  description?: string;
  link?: LinkProps;
  image?: ImageProps;
  videoUrl?: string;
  isImageRight?: boolean;
  backgroundColor?: BackgroundColors;
  isInCarousel?: boolean;
  imagePosition?: ImagePosition;
  valueToBeReplacedObj?: Record<string, string>;
};

export default function ImageLeftTextRight({
  eyebrow,
  title,
  description,
  link,
  image,
  videoUrl,
  isImageRight,
  backgroundColor,
  isInCarousel,
  valueToBeReplacedObj,
}: ImageLeftTextRightProps) {
  return (
    <div
      className={clsx(
        'flex flex-col w-full whitespace-normal md:flex-row md:gap-10 lg:gap-32',
        isImageRight ? 'md:flex-row-reverse' : 'md:flex-row',
        getBackgroundSectionClassName(backgroundColor),
        videoUrl && 'mt-12 md:my-12'
      )}
    >
      <div
        className={clsx(
          'w-full min-h-[250px] sm:min-w-[350px] lg:max-w-[50%]',
          isInCarousel
            ? 'h-[230px] lg:min-w-[520px] lg:h-[360px]'
            : 'md:max-w-[50%]',
          videoUrl && 'flex'
        )}
      >
        {image && (
          <ImageWithPlaceholder
            src={image.src}
            alt={image.alt}
            fill
            priority
            className="object-cover object-center"
            sizes="(min-width: 1140px) 50vw, (min-width: 940px) calc(70.56vw - 276px), (min-width: 780px) calc(17.14vw + 216px), 100vw"
          />
        )}

        {videoUrl && (
          <iframe className="w-full min-h-[350px]" src={videoUrl}></iframe>
        )}
      </div>
      <div
        className={clsx(
          'flex flex-col justify-center gap-7 md:gap-5',
          !isInCarousel &&
            'my-5 sm:my-10 md:max-w-1/2 lg:max-w-[430px] md:my-[3.75rem]',
          !isInCarousel && isImageRight && 'ml-4 md:ml-10',
          !isInCarousel && !isImageRight && 'mr-4 md:mr-10',
          videoUrl ? 'mx-0' : 'mx-4'
        )}
      >
        {eyebrow && (
          <Eyebrow
            className="mb-0 md:mt-2"
            eyebrow={{
              type: EyebrowType.Text,
              text: eyebrow,
            }}
          />
        )}
        {title && (
          <SecondaryHeading className={clsx('max-sm:text-[1.5rem]')}>
            {replaceKeysFromString(title, valueToBeReplacedObj)}
          </SecondaryHeading>
        )}
        {description && (
          <div
            dangerouslySetInnerHTML={{
              __html: replaceKeysFromString(description, valueToBeReplacedObj),
            }}
          />
        )}
        {link && (
          <SecondaryLinkButton
            href={link.url}
            className="mt-2 max-md:self-center"
          >
            {link.label}
          </SecondaryLinkButton>
        )}
      </div>
    </div>
  );
}
